<script setup lang="ts">
import type { SessionsCatalogRowResponse } from "@simbelapp/order-sdk";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import { useTrainingCatalogSessions } from "~/composables/trainings/useTrainingCatalogSessions";
import { ItemType } from "~/utils/enums/common.enums";
import CatalogSessionsTable from "./CatalogSessionsTable.vue";

/**
 * The two tables of « Gestion du catalogue > Sessions », shared by the external and the internal
 * training modals: what the plan already carries, and the dates merely offered to registration.
 *
 * One implementation for both, because the split only means anything if it reads the same on
 * either side — a row in the plan may have people booked on it, so its only action is a link out,
 * while a date nobody took can still be dropped. That is the promise the tooltip makes.
 */
withDefaults(
  defineProps<{
    inPlan: SessionsCatalogRowResponse[];
    notInPlan: SessionsCatalogRowResponse[];
    /**
     * Whether this admin may drop a date at all. Required, because `PATCH /sessions/cancelSession`
     * looks its session up by id alone — it checks the caller's role, never the company that owns
     * the training — so this is the only gate between a viewer and a session they do not own.
     */
    canDelete: boolean;
    /** Dates of a training synced from an external LMS are read-only here. */
    lmsDeleteBlocked?: boolean;
  }>(),
  { lmsDeleteBlocked: false },
);

const emit = defineEmits<{ deleteSession: [sessionId: string] }>();

const { notInPlanTooltip } = useTrainingCatalogSessions();
</script>

<template>
  <div class="flex flex-col gap-4">
    <section v-if="inPlan.length">
      <h4 class="body_demi">Sessions au plan</h4>
      <CatalogSessionsTable :rows="inPlan">
        <template #action="{ row }">
          <NuxtLink
            :to="`/sessions?id=${row.sessionId}&type=${ItemType.SESSION}`"
            target="_blank"
            title="Ouvrir la session"
            class="flex"
          >
            <SvgIcon name="link" class="h-[16px] w-[16px] text-primary-blue-color cursor-pointer" />
          </NuxtLink>
        </template>
      </CatalogSessionsTable>
    </section>

    <section v-if="notInPlan.length">
      <h4 class="body_demi">Dates disponibles</h4>
      <div class="flex items-center gap-1">
        <span class="small_med text-tertiary-color">Pas encore au plan</span>
        <Tooltip :position="'top-mid'" append-to-body :svg-width="16" class="shrink-0 cursor-pointer">
          <template #content>
            <div class="flex max-w-[420px] gap-2 items-center">
              <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
              <span class="body_regular whitespace-pre-line">{{ notInPlanTooltip }}</span>
            </div>
          </template>
        </Tooltip>
      </div>
      <CatalogSessionsTable :rows="notInPlan">
        <template #action="{ row }">
          <SvgIcon
            v-if="canDelete && !row.nbAttendees && !lmsDeleteBlocked"
            name="delete_trash"
            class="h-[16px] w-[16px] text-primary-red-color cursor-pointer"
            title="Supprimer la session"
            @click="emit('deleteSession', row.sessionId)"
          />
          <Tooltip v-else-if="canDelete && !row.nbAttendees" :position="'top-left'" minified append-to-body>
            <template #img>
              <SvgIcon name="delete_trash" class="h-[16px] w-[16px] text-secondary-color" />
            </template>
            <template #content>
              <div class="flex gap-[8px] items-center body_regular w-[338px]">
                <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
                <span>Cette action n'est pas disponible pour une session importée d'un LMS</span>
              </div>
            </template>
          </Tooltip>
        </template>
      </CatalogSessionsTable>
    </section>
  </div>
</template>
